import { Controller } from '@hotwired/stimulus';

const INPUT_ERROR_CLASSES = [
  'border-2',
  'border-danger-300',
  'focus-visible:ring-danger-500',
  'focus-visible:border-danger-500',
  'bg-danger-50',
];
const INPUT_NORMAL_CLASSES = [
  'border-neutral-300',
  'focus-visible:ring-primary-500',
  'focus-visible:border-primary-500',
];
const BUTTON_DISABLED_CLASSES = ['opacity-50', 'cursor-not-allowed', 'pointer-events-none'];

/**
 * Intercepts the review form submission to show a confirmation modal
 * with the legal attestation and eIDAS disclaimer before final submit.
 * Also validates that the payment date is not in the future.
 */
export default class extends Controller {
  static targets = ['modal', 'overlay', 'form', 'paymentDate', 'paymentDateError', 'submitButton'];

  connect() {
    this.confirmed = false;
    this.validatePaymentDate();
  }

  submit(event) {
    if (this.confirmed) {
      this.confirmed = false;
      return; // Let the form submit normally
    }
    event.preventDefault();
    if (!this.formTarget.reportValidity()) return;
    if (this.isPaymentDateInFuture()) return;
    this.openModal();
  }

  confirm() {
    this.closeModal();
    this.confirmed = true;
    this.formTarget.requestSubmit();
  }

  close() {
    this.closeModal();
  }

  closeOnOverlay(event) {
    if (event.target === this.overlayTarget) {
      this.closeModal();
    }
  }

  closeOnEscape(event) {
    if (event.key === 'Escape') {
      this.closeModal();
    }
  }

  validatePaymentDate() {
    if (!this.hasPaymentDateTarget) return;

    const isFuture = this.isPaymentDateInFuture();
    this.togglePaymentDateError(isFuture);
    this.toggleSubmitButton(isFuture);
  }

  // private

  isPaymentDateInFuture() {
    if (!this.hasPaymentDateTarget) return false;

    const value = this.paymentDateTarget.value;
    if (!value) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(value + 'T00:00:00');
    return selected > today;
  }

  togglePaymentDateError(show) {
    if (this.hasPaymentDateErrorTarget) {
      this.paymentDateErrorTarget.classList.toggle('hidden', !show);
    }

    if (this.hasPaymentDateTarget) {
      if (show) {
        this.paymentDateTarget.classList.remove(...INPUT_NORMAL_CLASSES);
        this.paymentDateTarget.classList.add(...INPUT_ERROR_CLASSES);
      } else {
        this.paymentDateTarget.classList.remove(...INPUT_ERROR_CLASSES);
        this.paymentDateTarget.classList.add(...INPUT_NORMAL_CLASSES);
      }
    }
  }

  toggleSubmitButton(disabled) {
    if (!this.hasSubmitButtonTarget) return;

    if (disabled) {
      this.submitButtonTarget.disabled = true;
      this.submitButtonTarget.classList.add(...BUTTON_DISABLED_CLASSES);
      this.submitButtonTarget.classList.remove('cursor-pointer');
    } else {
      this.submitButtonTarget.disabled = false;
      this.submitButtonTarget.classList.remove(...BUTTON_DISABLED_CLASSES);
      this.submitButtonTarget.classList.add('cursor-pointer');
    }
  }

  openModal() {
    this.modalTarget.classList.remove('hidden');
    document.addEventListener('keydown', this._escapeHandler);
  }

  closeModal() {
    this.modalTarget.classList.add('hidden');
    document.removeEventListener('keydown', this._escapeHandler);
  }

  get _escapeHandler() {
    if (!this.__escapeHandler) {
      this.__escapeHandler = (e) => this.closeOnEscape(e);
    }
    return this.__escapeHandler;
  }
}
